import { useState } from 'react';
import { ButtonLink, Card, Eyebrow, Heading, Lead, Section } from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

/**
 * Founder card.
 *
 * Spec 8: do not invent biographical facts, and do not scrape LinkedIn.
 *
 * **Status of the copy below: expanded draft, awaiting the founder's sign-off.**
 * The previous three paragraphs were supplied by the company and reproduced
 * verbatim. The founder then asked for them to be rewritten and elaborated, so
 * what follows is longer than anything that has been formally approved - and
 * the rule above still binds it. Every factual claim in it is carried over from
 * the approved text: the fifteen-plus years, and the named domains (software
 * architecture, application development, cloud, cybersecurity, system
 * integration, technical leadership, enterprise applications). What changed is
 * the framing and the depth, not the facts.
 *
 * **What is deliberately absent**, because it was never supplied and the
 * LinkedIn profile was not read: employer names, job titles, dates, education,
 * certifications, and any specific project or client. Those are exactly the
 * details a reader would most trust, which is why inventing plausible ones
 * would be the most damaging thing this file could do. Add them by pasting the
 * approved text, not by paraphrasing the profile.
 *
 * `bio` is a list of paragraphs rather than one string, so a future approval
 * can replace one paragraph without disturbing the rest.
 *
 * The photograph is **served from this repository**, not hotlinked.
 *
 * It was supplied twice as a `media.licdn.com` URL, and those URLs are signed
 * and dated: the `e=` parameter is a Unix expiry. The first lapsed on
 * 2026-09-03 and the card silently fell back to the monogram; the replacement
 * would have lapsed on 2026-10-08. Pointing the site at a link that stops
 * working in a fortnight is not a fix, it is the same outage rescheduled, so
 * the file was downloaded to `public/` and is referenced by path.
 *
 * Two problems go away with it. The link cannot expire, and nothing tells
 * LinkedIn which page loaded it - `referrerPolicy` below is now belt and
 * braces rather than the only thing preventing a referer leak.
 *
 * To replace the photograph, drop a new file in `public/` and change `photo`.
 * Do not paste a CDN URL back in.
 */
interface Founder {
  name: string;
  role: string;
  linkedin: string;
  /** Approved biography, one entry per paragraph. */
  bio: string[];
  /** The supplied "Focus:" line, split on its separators. */
  expertise: string[];
  /**
   * Supplied photograph, served from `public/`. Null renders a monogram.
   * Never a remote URL - see the note above.
   */
  photo: string | null;
}

const FOUNDER: Founder = {
  name: 'Yohan Perera',
  role: 'Founder',
  linkedin: 'https://www.linkedin.com/in/kdyohan/',
  bio: [
    'Yohan Perera is the Founder of WaveQA. He has spent more than fifteen ' +
      'years in the software industry, working across software architecture, ' +
      'application development, cloud technologies, cybersecurity, system ' +
      'integration, and technical leadership.',
    'That range is unusual, and it is the reason WaveQA looks the way it does. ' +
      'Architecture, security and integration are not separate concerns to ' +
      'someone who has had to hold all three at once on the same release: an ' +
      'integration boundary is where correctness and security questions arrive ' +
      'together, and an architecture is mostly a set of decisions about which ' +
      'failures you are willing to live with.',
    'His work has centred on designing and delivering scalable software ' +
      'solutions, and on the part of the job that is not code - working closely ' +
      'with development teams, clients and business stakeholders to turn what a ' +
      'business needs into something an engineering team can actually build, ' +
      'ship and keep running. Enterprise application development has been the ' +
      'constant: systems with real users, real integration surface, and real ' +
      'consequences when a release goes wrong.',
    'Quality assurance is where those consequences land. Teams under delivery ' +
      'pressure do not skip testing because they think it is unimportant; they ' +
      'skip it because building and maintaining the infrastructure to do it ' +
      'properly competes with shipping the product. WaveQA exists to remove ' +
      'that trade-off rather than to lecture anyone about it.',
    'At WaveQA, Yohan focuses on the product vision, business strategy, ' +
      'customer needs, and the practical application of AI in software quality ' +
      'assurance - with an emphasis on the word practical. The agent is built ' +
      'so that evidence decides a result and a model only ever proposes one, ' +
      'because a QA tool that is confidently wrong is worse than no QA tool at ' +
      'all.',
    'His vision is to make intelligent QA automation accessible to development ' +
      'teams without requiring them to build complex testing infrastructure ' +
      'themselves: point the agent at the application, give it the acceptance ' +
      'criteria, and get back a result backed by evidence rather than an ' +
      'opinion.',
  ],
  expertise: [
    'Product Vision',
    'Strategy',
    'AI-Powered QA',
    'Software Architecture',
    'Enterprise Applications',
    'Cloud Technologies',
    'Cybersecurity',
    'System Integration',
    'Technology Leadership',
    'Customer Experience',
  ],
  photo: '/founder-yohan-perera.jpg',
};

export function About() {
  usePageTitle('About Us — The People Behind WaveQA');

  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>WaveQA Agent — Founder</Eyebrow>
        <Heading level={1} className="text-white">
          About Us — The People Behind WaveQA
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          WaveQA is built by a team with a strong background in software
          engineering, architecture, AI, and modern software delivery. Our goal is
          simple: to make software quality assurance smarter, more reliable, and
          easier to integrate into the software development lifecycle.
        </Lead>
      </Section>

      <Section className="border-b border-hull-800">
        <Heading className="text-white">Meet the Founder</Heading>

        {/* Full width, as asked. The card lays itself out in two columns at
            `lg` so the prose keeps a readable measure instead of running the
            whole way across a wide display. */}
        <div className="mt-8">
          <FounderCard founder={FOUNDER} />
        </div>
      </Section>

      <Section className="border-b border-hull-800">
        <Heading className="text-white">Building the Future of Software Quality</Heading>
        <Lead className="mt-5 max-w-3xl">
          Together, the WaveQA team is working toward a new approach to software
          quality assurance — where AI agents can understand requirements, interact
          with real applications, validate acceptance criteria, identify issues, and
          provide evidence-based results.
        </Lead>

        <p className="mt-6 max-w-3xl text-base text-hull-300">
          WaveQA is designed with one principle in mind:
        </p>

        {/* The closing line is the product thesis, so it is set as a quotation
            rather than another paragraph - it is the one sentence a visitor
            should still have in mind after leaving the page. */}
        <blockquote className="mt-5 max-w-3xl border-l-2 border-surge-500 pl-5">
          <p className="text-lg font-medium leading-relaxed text-white sm:text-xl">
            Let developers build the software. Let intelligent agents help prove
            that it works.
          </p>
        </blockquote>
      </Section>

      <Section>
        <Card className="text-center">
          <Heading className="text-white">Work with us</Heading>
          <Lead className="mx-auto mt-3 max-w-xl">
            Questions about deployment, security review, or whether WaveQA fits
            your process?
          </Lead>
          <div className="mt-7">
            <ButtonLink to="/contact">Get in touch</ButtonLink>
          </div>
        </Card>
      </Section>
    </>
  );
}

function FounderCard({ founder }: { founder: Founder }) {
  // Falls back to a monogram if the file is missing, so a bad deploy shows a
  // monogram rather than a broken-image icon. It no longer guards against an
  // expiring CDN link, because there is no longer a CDN link to expire.
  const [photoFailed, setPhotoFailed] = useState(false);

  const initials = founder.name
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (
    // Two columns from `lg`: identity and links in a fixed rail, prose in the
    // rest. The card spans the page, but the biography still wraps at a
    // readable measure rather than stretching across a wide monitor - which is
    // the failure mode a full-width text block usually has.
    <Card className="grid gap-8 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] lg:gap-12">
      <div className="lg:border-r lg:border-hull-700 lg:pr-10">
        {/* Photo and name sit side by side when stacked on a phone, and above
            one another once the rail has a column to itself. */}
        <div className="flex items-start gap-4 sm:items-center lg:flex-col lg:items-start">
          {founder.photo && !photoFailed ? (
            <img
              src={founder.photo}
              alt={`${founder.name}, ${founder.role}`}
              className="h-20 w-20 shrink-0 rounded-full border border-hull-600 object-cover lg:h-28 lg:w-28"
              width={112}
              height={112}
              loading="lazy"
              decoding="async"
              // Do not tell the image host which page loaded it.
              referrerPolicy="no-referrer"
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <div
              aria-hidden="true"
              className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-hull-600 bg-hull-700 text-xl font-semibold text-surge-300 lg:h-28 lg:w-28 lg:text-3xl"
            >
              {initials}
            </div>
          )}

          <div className="min-w-0 lg:mt-5">
            <h3 className="text-lg font-semibold text-white lg:text-xl">{founder.name}</h3>
            <p className="mt-1 text-sm font-medium text-surge-400">{founder.role}</p>
          </div>
        </div>

        {founder.expertise.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-hull-500">
              Focus
            </p>
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {founder.expertise.map((skill) => (
                <li
                  key={skill}
                  className="rounded-full border border-hull-700 bg-hull-900/60 px-2.5 py-1 text-xs text-hull-300"
                >
                  {skill}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-7">
          {/* The profile link sits here rather than as a URL inside the
              biography, so the copy stays readable and the card keeps one
              clear action. */}
          <ButtonLink to={founder.linkedin} variant="secondary">
            View LinkedIn Profile
          </ButtonLink>
        </div>
      </div>

      <div className="max-w-3xl space-y-4">
        {founder.bio.map((paragraph) => (
          <p key={paragraph.slice(0, 40)} className="text-base leading-relaxed text-hull-300">
            {paragraph}
          </p>
        ))}
      </div>
    </Card>
  );
}
